import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useQuery as useConvexQuery } from "convex/react";
import { Info } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { LoadingPanel } from "~/components/shared/loading-panel";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import { Combobox } from "~/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import {
  optionalSearchString,
  requiredSearchString,
} from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";

interface SalesMenuItemMappingEntry {
  menuId: string;
  menuName: string;
  items: { itemId: string; itemName: string; itemPrice: string }[];
}

const removeRef = api.salesCategories.remove;

const getPreDeleteDataRef = api.salesCategories.getPreDeleteData;

const getAllRef = api.salesCategories.getAll;

const addItemsRef = api.salesCategories.addItems;

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/settings/sales-categories/$id/delete"
)({
  component: SalesCategoryDeleteModal,
  validateSearch: (
    search: Record<string, unknown>
  ): { name: string; newSalesCategoryId?: string } => ({
    name: requiredSearchString(search.name),
    newSalesCategoryId: optionalSearchString(search.newSalesCategoryId),
  }),
});

function SalesCategoryDeleteModal() {
  const navigate = useNavigate();
  const { id } = useParams({
    from: "/_authenticated/settings/sales-categories/$id/delete",
  });
  const { name, newSalesCategoryId } = useSearch({
    from: "/_authenticated/settings/sales-categories/$id/delete",
  });

  const handleClose = () => {
    void navigate({ to: "/settings/sales-categories" });
  };

  // V4.1: Navigation callback for inline category creation
  const handleCreateSalesCategory = (categoryName: string) => {
    void navigate({
      to: `/settings/sales-categories/${id}/delete/create-category`,
      search: { name: categoryName, deletingId: id, deletingName: name },
    });
  };

  const handleClearNewCategoryId = () => {
    void navigate({
      to: `/settings/sales-categories/${id}/delete`,
      search: { name },
      replace: true,
    });
  };

  return (
    <>
      <Dialog onOpenChange={(open) => !open && handleClose()} open>
        <SalesCategoriesDeleteModalContent
          newSalesCategoryId={newSalesCategoryId}
          onClearNewCategoryId={handleClearNewCategoryId}
          onCreateSalesCategory={handleCreateSalesCategory}
          onModalClose={handleClose}
          salesCategory={{ id, name }}
        />
      </Dialog>
      {/* V4.1: Outlet for child routes (e.g., /create-category modal) */}
      <Outlet />
    </>
  );
}

const SalesCategoriesDeleteModalContent = ({
  salesCategory,
  onModalClose,
  newSalesCategoryId,
  onCreateSalesCategory,
  onClearNewCategoryId,
}: {
  salesCategory: {
    id: string;
    name: string;
  };
  onModalClose: () => void;
  // V4.1: Route-based category creation props
  newSalesCategoryId?: string;
  onCreateSalesCategory: (name: string) => void;
  onClearNewCategoryId: () => void;
}) => {
  const [showReplaceModal, setShowReplaceModal] = useState(false);

  const salesCategoryDeleteMutation = useMutation({
    mutationFn: ({ id }: { id: Id<"salesCategories"> }) => {
      return convexClient.mutation(removeRef, { id });
    },
    onSuccess: () => {
      ProductEvents.salesCategoryDeleted(salesCategory.id);
    },
  });

  const salesCategoryDelete = async (id: string) => {
    try {
      await salesCategoryDeleteMutation.mutateAsync({
        id: toConvexTableId("salesCategories", id),
      });
      toast.success("Sales Category deleted successfully");
      if (itemIds.length > 0) {
        setShowReplaceModal(true);
      } else {
        onModalClose();
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to delete sales category"
      );
    }
  };

  const menus = useConvexQuery(getPreDeleteDataRef, {
    id: toConvexTableId("salesCategories", salesCategory.id),
  });

  const itemIds = useMemo(() => {
    if (!menus) {
      return [];
    }

    const idSet = new Set<string>();
    for (const menu of menus) {
      for (const item of menu.items) {
        idSet.add(item.itemId);
      }
    }
    return Array.from(idSet);
  }, [menus]);

  if (showReplaceModal && menus) {
    return (
      <SalesCategoriesReplaceModalContent
        itemIds={itemIds}
        menus={menus}
        newSalesCategoryId={newSalesCategoryId}
        onClearNewCategoryId={onClearNewCategoryId}
        onCreateSalesCategory={onCreateSalesCategory}
        onModalClose={onModalClose}
      />
    );
  }

  return (
    <DialogContent
      className="w-full max-w-120"
      noCloseButton
      noScrollPrevention
    >
      <DialogHeader className="items-center space-y-0">
        <DialogTitle className="text-foreground">
          Delete Sales Category
        </DialogTitle>
        <DialogDescription className="body text-muted-foreground text-center">
          Are you sure you want to delete this sales category? All items using
          this sales category will be untaxed.
        </DialogDescription>
      </DialogHeader>

      <div className="bg-muted text-foreground flex rounded-lg p-4">
        <p>{salesCategory.name}</p>
      </div>
      <AffectedItems itemsNumber={itemIds.length} menus={menus} />
      <div className="flex w-full justify-between space-x-3">
        <Button
          className="w-full"
          onClick={onModalClose}
          size="lg"
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
        <Button
          className="w-full"
          disabled={salesCategoryDeleteMutation.isPending}
          onClick={async () => {
            await salesCategoryDelete(salesCategory.id);
          }}
          size="lg"
          type="button"
          variant="destructive"
        >
          Delete
        </Button>
      </div>
    </DialogContent>
  );
};

const SalesCategoriesReplaceModalContent = ({
  itemIds,
  menus,
  onModalClose,
  newSalesCategoryId,
  onCreateSalesCategory,
  onClearNewCategoryId,
}: {
  itemIds: string[];
  menus: SalesMenuItemMappingEntry[];
  onModalClose: () => void;
  newSalesCategoryId?: string;
  onCreateSalesCategory: (name: string) => void;
  onClearNewCategoryId: () => void;
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const salesCategories = useConvexQuery(getAllRef);

  // V4.1: Handle newly created category from child route
  useEffect(() => {
    if (newSalesCategoryId) {
      setSelectedCategory(newSalesCategoryId);
      onClearNewCategoryId();
    }
  }, [newSalesCategoryId, onClearNewCategoryId]);

  const addItemsToCategoryMutation = useMutation({
    mutationFn: (data: {
      categoryId: Id<"salesCategories">;
      items: Id<"items">[];
    }) => {
      return convexClient.mutation(addItemsRef, data);
    },
    onSuccess: () => {
      toast.success("The items were successfully updated");
      onModalClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to update items");
    },
  });

  const addItemsToCategory = async (data: {
    categoryId: Id<"salesCategories">;
    items: Id<"items">[];
  }) => {
    setError(null);
    await addItemsToCategoryMutation.mutateAsync(data);
  };

  if (!salesCategories) {
    return <LoadingPanel message="Loading sales categories..." />;
  }

  return (
    <DialogContent
      className="w-full max-w-120"
      noCloseButton
      noScrollPrevention
    >
      <DialogHeader className="items-center space-y-0">
        <DialogTitle className="text-foreground">
          Replace Sales Category
        </DialogTitle>
        <DialogDescription className="body text-muted-foreground text-center">
          Do you want to apply another sales category to affected items?
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label>New Sales Category for Items</Label>
        <Combobox
          createNewOption={{
            label: "Create new category",
            onClick: onCreateSalesCategory,
          }}
          dropdownClassName="w-96"
          items={salesCategories.map((c) => ({
            label: c.name,
            value: c._id,
          }))}
          onValueChange={setSelectedCategory}
          value={selectedCategory}
        />
        {error && (
          <p className="body-sm text-destructive dark:text-destructive flex gap-2 leading-tight">
            <Info className="text-destructive size-4" />
            {error}
          </p>
        )}
      </div>
      <AffectedItems itemsNumber={itemIds.length} menus={menus} />
      <div className="flex w-full justify-between space-x-3">
        <Button
          className="w-full"
          onClick={onModalClose}
          size="lg"
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
        <Button
          className="w-full"
          disabled={addItemsToCategoryMutation.isPending}
          onClick={async () => {
            if (!selectedCategory) {
              return setError(
                "Select a new sales category to assign to affected items"
              );
            }
            await addItemsToCategory({
              categoryId: toConvexTableId("salesCategories", selectedCategory),
              items: itemIds.map((id) => toConvexTableId("items", id)),
            });
          }}
          size="lg"
          type="button"
        >
          Save
        </Button>
      </div>
    </DialogContent>
  );
};

const AffectedItems = ({
  menus,
  itemsNumber,
}: {
  menus?: SalesMenuItemMappingEntry[];
  itemsNumber: number;
}) => {
  if (itemsNumber === 0 || !menus) {
    return null;
  }

  return (
    <Accordion type="multiple">
      <AccordionItem className="border-b-0" value="menu-items">
        <AccordionTrigger className="label text-foreground/70 leading-tight hover:no-underline">
          Affected items ({itemsNumber})
        </AccordionTrigger>
        <AccordionContent className="space-y-3">
          {menus.map((menu) => (
            <div key={menu.menuId}>
              <p className="body-sm text-foreground/70 leading-tight">
                {menu.menuName}
              </p>
              <div className="mx-2 mt-2 space-y-1">
                {menu.items.map((item) => (
                  <p
                    className="body-sm text-muted-foreground leading-tight"
                    key={item.itemId}
                  >
                    {item.itemName}{" "}
                    {Number.parseFloat(item.itemPrice).toFixed(2)} %
                  </p>
                ))}
              </div>
            </div>
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
